import { ColorEnum } from '@/constants/color'
import { FontEnum } from '@/constants/font'
import { isNumeric } from '@/utils/isNumeric'
import { FC, useRef, useState } from 'react'
import {
    StyleProp,
    StyleSheet,
    Text,
    TextInput,
    TouchableOpacity,
    View,
    ViewStyle,
} from 'react-native'

interface CodeEntryFieldProps {
    length: number
    onSubmitEditing: (code: string) => Promise<void> | void
    style?: StyleProp<ViewStyle>
}

const CodeEntryField: FC<CodeEntryFieldProps> = ({ length, onSubmitEditing, style }) => {
    const [code, setCode] = useState('')
    const [loading, setLoading] = useState(false)
    const [focused, setFocused] = useState(true)

    const inputRef = useRef<TextInput>(null)

    const handleRectanglePress = () => {
        if (loading) return

        inputRef.current?.blur()

        setTimeout(() => {
            inputRef.current?.focus()
        }, 100)
    }

    const handleChangeText = (code: string) => {
        if (!isNumeric(code)) return

        if (code.length - 1 >= length) return

        setCode(code)
    }

    const handleSubmitEditing = async () => {
        if (loading) return

        try {
            setLoading(true)

            await onSubmitEditing(code)
        } finally {
            setLoading(false)
        }
    }

    return (
        <View style={[styles.container, style]}>
            {Array.from({ length }, (_, i) => {
                const isActive = focused && !loading && i === code.length

                return (
                    <TouchableOpacity
                        key={i}
                        onPress={handleRectanglePress}
                        style={[
                            styles.rectangle,
                            isActive && styles.rectangleActive,
                            loading && styles.rectangleDisabled,
                        ]}
                    >
                        {code[i] ? (
                            <Text
                                style={[
                                    styles.rectangleText,
                                    loading && styles.rectangleTextDisabled,
                                ]}
                            >
                                {code[i]}
                            </Text>
                        ) : (
                            isActive && <View style={styles.cursor} />
                        )}
                    </TouchableOpacity>
                )
            })}
            <TextInput
                autoFocus
                editable={!loading}
                inputMode="numeric"
                returnKeyType="done"
                value={code}
                onChangeText={handleChangeText}
                onSubmitEditing={handleSubmitEditing}
                onFocus={() => setFocused(true)}
                onBlur={() => setFocused(false)}
                ref={inputRef}
                caretHidden
                style={styles.hiddenInput}
            />
        </View>
    )
}

const styles = StyleSheet.create({
    container: {
        flexDirection: 'row',
        gap: 8,
    },
    rectangle: {
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: ColorEnum.WHITE,
        borderColor: ColorEnum.GRAY_300,
        borderWidth: 1,
        borderRadius: 8,
        height: 48,
        width: 32,
    },
    rectangleActive: {
        borderColor: ColorEnum.BRICK_700,
        elevation: 4,
        shadowColor: ColorEnum.BRICK_600,
        shadowOffset: {
            height: 0,
            width: 0,
        },
        shadowOpacity: 1,
        shadowRadius: 4,
    },
    rectangleText: {
        color: ColorEnum.JET_900,
        fontFamily: FontEnum.SatoshiRegular,
        fontSize: 14,
    },
    rectangleDisabled: {
        backgroundColor: ColorEnum.GRAY_200,
    },
    rectangleTextDisabled: {
        color: ColorEnum.GRAY_400,
    },
    cursor: {
        width: 1,
        height: 24,
        backgroundColor: ColorEnum.BLUE_400,
    },
    hiddenInput: {
        opacity: 0,
        position: 'absolute',
    },
})

export default CodeEntryField
